import { Query } from '@apollo/client/react/components';
import { withApollo } from '@apollo/client/react/hoc';
import { InitGA } from '@bnnvara/analytics/src/components/GoogleAnalytics/InitGA';
import { track } from '@bnnvara/analytics/src/track';
import { BrandContext } from '@bnnvara/brand';
import { FilterSelect } from '@bnnvara/forms/src/components/Select/FilterSelect/FilterSelect';
import SearchBar from '@bnnvara/search';
import debounce from 'lodash/debounce';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import omitBy from 'lodash/omitBy';
import set from 'lodash/set';
import getConfig from 'next/config';
import Router, { withRouter } from 'next/router';
import PropTypes from 'prop-types';
import React from 'react';

import { FetchBrands } from '../../../api/queries/brand.gql';
import { SearchBrandQuery, SearchQuery } from '../../../api/queries/search.gql';
import FEATURE_FLAGS from '../../../hocs/withBrandedComposablePage/featureFlags';
import removeQueryParams from '../../Link/helpers/removeQueryParams';
import setQueryParams from '../../Link/helpers/setQueryParams';
import SearchResults from '../SearchResults';
import * as S from './styled';

class SearchPage extends React.Component {
  constructor(props) {
    super(props);
    this.searchBarRef = React.createRef();
    this.numberOfItemsPerPage = 8;

    const {
      router: {
        query: { query = '', size = this.numberOfItemsPerPage },
      },
    } = this.props;

    this.state = {
      inputText: query,
      query,
      from: 0,
      size: parseInt(size, 10),
      initialRequest: true,
      selectedBrand: '',
    };

    this.updateQueryDebounced = debounce(
      (...args) => this.updateQuery.apply(null, args),
      300
    );
  }

  componentDidMount() {
    Router.events.on('routeChangeComplete', this.handleRouteChange);
  }

  shouldComponentUpdate(nextProps, nextState) {
    const { query, inputText, initialLoading, size } = this.state;
    const {
      router: { query: parameters },
    } = this.props;

    return (
      size !== parameters.size ||
      parameters.query !== nextProps.router.query.query ||
      query !== nextState.query ||
      nextState.inputText !== inputText ||
      nextState.initialLoading !== initialLoading
    );
  }

  componentDidUpdate(prevProps, prevState) {
    const { query, size } = this.state;

    if (query && query !== prevState.query) {
      this.updateRoute(query, size);
    }
  }

  componentWillUnmount() {
    const {
      router: { query },
    } = this.props;

    Router.events.off('routeChangeComplete', this.handleRouteChange);
    removeQueryParams({ slug: query.brandSlug })(
      ['query', 'size', 'contained'],
      'replace'
    );
  }

  updateRoute = (
    query = '',
    size = this.numberOfItemsPerPage,
    type = 'push'
  ) => {
    const {
      router: { query: originalQueryParams },
    } = this.props;

    const nextQueryParameters = omitBy(
      {
        ...originalQueryParams,
        query,
        size,
      },
      isNil
    );

    setQueryParams({
      slug: originalQueryParams.brandSlug,
    })(nextQueryParameters, type);
  };

  handleRouteChange = () => {
    const {
      router: { query: parameters },
    } = this.props;

    const { query } = this.state;

    if (query !== parameters.query) {
      this.handleTextChange(parameters.query, false);
    }
  };

  handleTextChange = (text = '', debounceSearch = true) => {
    const nextState = {
      inputText: text,
      from: 0,
      size: this.numberOfItemsPerPage,
    };

    if (!debounceSearch) {
      nextState.query = text;
    }

    this.setState(nextState);

    if (debounceSearch) {
      this.updateQueryDebounced();
    } else {
      this.updateQuery();
    }
  };

  updateQuery = () =>
    this.setState(({ inputText, query }) => ({
      query: inputText.length > 0 ? inputText : query,
    }));

  handleLoadedMore = (previousResult, { fetchMoreResult }) => {
    const { query } = this.state;

    const data = {
      ...previousResult,
    };

    if (data.brand) {
      set(data, 'brand.search.hits', fetchMoreResult.brand.search.hits);
      set(data, 'brand.search.results', [
        ...previousResult.brand.search.results,
        ...fetchMoreResult.brand.search.results,
      ]);
      this.updateRoute(query, data.brand.search.results.length, 'replace');
    } else {
      set(data, 'search.hits', fetchMoreResult.search.hits);
      set(data, 'search.results', [
        ...previousResult.search.results,
        ...fetchMoreResult.search.results,
      ]);
      this.updateRoute(query, data.search.results.length, 'replace');
    }
    track('Zoeken', 'click', 'Toon-meer-zoekresultaten');
    return data;
  };

  renderResults = ({ loading, networkStatus, data, fetchMore }) => {
    const { query, inputText, initialRequest } = this.state;
    const loadingMore = networkStatus === 3;

    if (!data) {
      return null;
    }

    const searchResult = data.brand ? data.brand.search : data.search;
    const hits = get(searchResult, 'hits', 0);
    const results = get(searchResult, 'results', []);
    const initialLoading = initialRequest && results.length === 0;

    if (!inputText || !query) {
      return null;
    }
    const { publicRuntimeConfig } = getConfig();
    return (
      <React.Fragment>
        <InitGA id={publicRuntimeConfig.googleAnalytics.id} />
        <SearchResults
          query={query}
          hits={hits}
          results={results}
          initialLoading={initialLoading}
          loadingMore={loadingMore}
          loading={loading}
          onLoadMore={() => {
            fetchMore({
              variables: {
                from: results.length,
                size: this.numberOfItemsPerPage,
              },
              updateQuery: this.handleLoadedMore,
            });
          }}
        />
      </React.Fragment>
    );
  };

  setBrand = (selectedBrand) => {
    track('Zoeken', 'filterTitle', `${selectedBrand}`);
    this.setState({
      selectedBrand,
    });
  };

  static sortedData = (dataBrands) =>
    [...dataBrands].sort((a, b) => {
      if (a.name < b.name) {
        return -1;
      }
      if (a.name > b.name) {
        return 1;
      }
      return 0;
    });

  render() {
    const { initialRequest, inputText, query, from, size } = this.state;

    const {
      router: {
        query: { contained: containedSearchFromUrl },
      },
    } = this.props;

    return (
      <BrandContext.Consumer>
        {(brand) => {
          const { selectedBrand } = this.state;
          let searchQuery = SearchQuery;
          const variables = { query, size, from };

          const isBranded = selectedBrand;

          if (!brand) {
            return null;
          }

          const containedSearch = brand.featureFlags.includes(
            FEATURE_FLAGS.containedSearch
          );

          if (containedSearch || isBranded || containedSearchFromUrl) {
            searchQuery = SearchBrandQuery;
            variables.brandSlug = selectedBrand || brand.slug;
          }

          return (
            <S.SearchContainer>
              {!containedSearch && (
                <Query query={FetchBrands}>
                  {({ loading, error, data }) => {
                    if (loading || error) return null;
                    return (
                      <S.FilterContainer>
                        <FilterSelect
                          onChange={(event) =>
                            this.setBrand(event.target.value)
                          }
                          value={selectedBrand}
                          text="Filter op titel: "
                        >
                          <option value="">Alles</option>
                          {SearchPage.sortedData(data.brands).map(
                            ({ name, slug }) => (
                              <option value={slug} key={slug}>
                                {name}
                              </option>
                            )
                          )}
                        </FilterSelect>
                      </S.FilterContainer>
                    );
                  }}
                </Query>
              )}
              <SearchBar
                ref={this.searchBarRef}
                onChange={this.handleTextChange}
                query={inputText}
                autoFocus
              />
              <S.SearchResultsContainer>
                <div>
                  <Query
                    skip={!query}
                    query={searchQuery}
                    ssr={false}
                    variables={variables}
                    fetchPolicy="network-only"
                    notifyOnNetworkStatusChange
                    onCompleted={() =>
                      initialRequest &&
                      this.setState({
                        initialRequest: false,
                      })
                    }
                  >
                    {this.renderResults}
                  </Query>
                </div>
              </S.SearchResultsContainer>
            </S.SearchContainer>
          );
        }}
      </BrandContext.Consumer>
    );
  }
}

SearchPage.propTypes = {
  router: PropTypes.shape({
    asPath: PropTypes.string,
    query: PropTypes.shape({
      brandSlug: PropTypes.string,
      query: PropTypes.string,
      size: PropTypes.string,
      contained: PropTypes.string,
    }),
  }).isRequired,
  client: PropTypes.shape({
    query: PropTypes.func,
  }).isRequired,
};

export default withRouter(withApollo(SearchPage));
